/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListMcpServerToolMetadataRequest,
  ListMcpServerToolMetadataSecurity,
} from "../models/operations/listmcpservertoolmetadata.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListMcpServerToolMetadataQuery,
  ListMcpServerToolMetadataQueryData,
  prefetchListMcpServerToolMetadata,
  queryKeyListMcpServerToolMetadata,
} from "./listMcpServerToolMetadata.core.js";
export {
  buildListMcpServerToolMetadataQuery,
  type ListMcpServerToolMetadataQueryData,
  prefetchListMcpServerToolMetadata,
  queryKeyListMcpServerToolMetadata,
};

export type ListMcpServerToolMetadataQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listToolMetadata mcpServers
 *
 * @remarks
 * List stored tool metadata for an MCP server.
 */
export function useListMcpServerToolMetadata(
  request: ListMcpServerToolMetadataRequest,
  security?: ListMcpServerToolMetadataSecurity | undefined,
  options?: QueryHookOptions<
    ListMcpServerToolMetadataQueryData,
    ListMcpServerToolMetadataQueryError
  >,
): UseQueryResult<
  ListMcpServerToolMetadataQueryData,
  ListMcpServerToolMetadataQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildListMcpServerToolMetadataQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listToolMetadata mcpServers
 *
 * @remarks
 * List stored tool metadata for an MCP server.
 */
export function useListMcpServerToolMetadataSuspense(
  request: ListMcpServerToolMetadataRequest,
  security?: ListMcpServerToolMetadataSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListMcpServerToolMetadataQueryData,
    ListMcpServerToolMetadataQueryError
  >,
): UseSuspenseQueryResult<
  ListMcpServerToolMetadataQueryData,
  ListMcpServerToolMetadataQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListMcpServerToolMetadataQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListMcpServerToolMetadataData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      mcpServerId: string;
      includeDeleted?: boolean | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ListMcpServerToolMetadataQueryData,
): ListMcpServerToolMetadataQueryData | undefined {
  const key = queryKeyListMcpServerToolMetadata(...queryKeyBase);

  return client.setQueryData<ListMcpServerToolMetadataQueryData>(key, data);
}

export function invalidateListMcpServerToolMetadata(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      mcpServerId: string;
      includeDeleted?: boolean | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "mcpServers",
      "listToolMetadata",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllListMcpServerToolMetadata(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpServers", "listToolMetadata"],
  });
}
